/**
 * Form Preview Component
 *
 * Shows a live preview of the form as the user builds it.
 * Supports both mobile and desktop preview modes.
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Send,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';
import { FormQuestion, FormConfig, DeviceMode } from './hooks/useFormBuilder';
import { getQuestionTypeInfo } from '@/lib/forms/question-types';

interface FormPreviewProps {
  form: FormConfig | null;
  questions: FormQuestion[];
  deviceMode: DeviceMode;
  onDeviceModeChange?: (mode: DeviceMode) => void;
  editingEnabled?: boolean;
  onUpdateQuestion?: (id: string, updates: Partial<FormQuestion>) => void;
  onUpdateUIText?: (key: keyof FormConfig['uiTextConfig'], value: string) => void;
  className?: string;
}

export function FormPreview({
  form,
  questions,
  deviceMode,
  onDeviceModeChange,
  editingEnabled = false,
  onUpdateQuestion,
  onUpdateUIText,
  className,
}: FormPreviewProps) {
  const [currentStep, setCurrentStep] = useState<'rating' | 'questions' | 'thankyou'>('rating');
  const [rating, setRating] = useState<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  // Get questions for current rating path
  const visibleQuestions = useMemo(() => {
    const isHighRating = rating >= 4;
    return questions.filter(q => {
      if (isHighRating) return q.visibleOnHighRating ?? true;
      return q.visibleOnLowRating ?? true;
    });
  }, [questions, rating]);

  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const progress = currentStep === 'rating'
    ? 0
    : currentStep === 'thankyou'
    ? 100
    : ((currentQuestionIndex + 1) / (visibleQuestions.length + 1)) * 100;

  // Step indicator calculations
  // Total steps: 1 (rating) + questions + 1 (thank you)
  const totalSteps = 2 + visibleQuestions.length;
  const currentStepNumber = currentStep === 'rating'
    ? 1
    : currentStep === 'thankyou'
    ? totalSteps
    : 2 + currentQuestionIndex;

  const canGoBackStep = currentStep !== 'rating';
  const canGoNextStep = currentStep !== 'thankyou';

  const handleStepBack = () => {
    if (currentStep === 'thankyou') {
      if (visibleQuestions.length > 0) {
        setCurrentStep('questions');
        setCurrentQuestionIndex(visibleQuestions.length - 1);
      } else {
        setCurrentStep('rating');
        setRating(0);
      }
    } else if (currentStep === 'questions') {
      handleBack();
    }
  };

  const handleStepNext = () => {
    if (currentStep === 'rating') {
      // Can't go next from rating without selecting
      return;
    } else if (currentStep === 'questions') {
      handleNext();
    }
  };

  const handleRatingSelect = (value: number) => {
    setRating(value);
    if (visibleQuestions.length > 0) {
      setCurrentStep('questions');
    } else {
      setCurrentStep('thankyou');
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setCurrentStep('thankyou');
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      setCurrentStep('rating');
      setRating(0);
    }
  };

  const handleReset = () => {
    setCurrentStep('rating');
    setRating(0);
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const isMobile = deviceMode === 'mobile';

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Preview Container */}
      <div className="flex-1 flex items-center justify-center py-4">
        {isMobile ? (
          /* iPhone-style Frame - Responsive */
          <div className="relative flex items-center justify-center h-full max-h-[600px]">
            {/* Phone Housing - scales to fit available space with aspect ratio */}
            <div
              className="relative bg-[#1a1a1a] dark:bg-[#2a2a2a] rounded-[1.5rem] sm:rounded-[2rem] p-1.5 sm:p-2 ring-1 ring-white/10 dark:ring-white/20 h-full max-w-[320px]"
              style={{
                aspectRatio: '9 / 19.5'
              }}
            >
              {/* Side Buttons - Hidden on small screens */}
              <div className="hidden sm:block absolute left-[-3px] top-[15%] w-[3px] h-8 bg-[#2a2a2a] dark:bg-[#3a3a3a] rounded-l-sm" />
              <div className="hidden sm:block absolute left-[-3px] top-[22%] w-[3px] h-12 bg-[#2a2a2a] dark:bg-[#3a3a3a] rounded-l-sm" />
              <div className="hidden sm:block absolute left-[-3px] top-[32%] w-[3px] h-12 bg-[#2a2a2a] dark:bg-[#3a3a3a] rounded-l-sm" />
              {/* Side Button - Right */}
              <div className="hidden sm:block absolute right-[-3px] top-[20%] w-[3px] h-16 bg-[#2a2a2a] dark:bg-[#3a3a3a] rounded-r-sm" />

              {/* Screen Container - Absolute positioned so content can't affect phone size */}
              <div className="absolute inset-1.5 sm:inset-2 bg-background rounded-[1.25rem] sm:rounded-[1.5rem] overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
                {/* Dynamic Island */}
                <div className="absolute top-1.5 sm:top-2 left-1/2 -translate-x-1/2 z-20">
                  <div className="bg-black rounded-full w-[70px] sm:w-[90px] h-[22px] sm:h-[28px] flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#1a1a1a] ring-1 ring-white/10" />
                  </div>
                </div>

                {/* Screen Content */}
                <div className="h-full min-h-0 flex flex-col pt-8 sm:pt-10">
                  {/* Progress Bar */}
                  {form?.progressBarConfig?.enabled && (
                    <div className="px-6 pb-0">
                      <Progress value={progress} className="h-1" />
                    </div>
                  )}

                  {/* Step Indicator */}
                  {form?.stepIndicatorConfig?.enabled && (
                    <div className="flex items-center justify-center gap-3 px-6 py-2">
                      {form.stepIndicatorConfig.showArrows && (
                        <button
                          onClick={handleStepBack}
                          disabled={!canGoBackStep}
                          className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                      )}
                      <span className="text-sm text-muted-foreground font-medium">
                        Step {currentStepNumber} / {totalSteps}
                      </span>
                      {form.stepIndicatorConfig.showArrows && (
                        <button
                          onClick={handleStepNext}
                          disabled={!canGoNextStep || currentStep === 'rating'}
                          className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Content Area */}
                  <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <AnimatePresence mode="wait">
                      {currentStep === 'rating' && (
                        <RatingStep
                          key="rating"
                          rating={rating}
                          onSelect={handleRatingSelect}
                          title={form?.uiTextConfig?.ratingTitle || 'How was your experience?'}
                          subtitle={form?.uiTextConfig?.ratingSubtitle || 'Tap a star to rate'}
                          isMobile={isMobile}
                          editingEnabled={editingEnabled}
                          onUpdateTitle={onUpdateUIText ? (value) => onUpdateUIText('ratingTitle', value) : undefined}
                          onUpdateSubtitle={onUpdateUIText ? (value) => onUpdateUIText('ratingSubtitle', value) : undefined}
                        />
                      )}

                      {currentStep === 'questions' && currentQuestion && (
                        <QuestionStep
                          key={currentQuestion.id}
                          question={currentQuestion}
                          questionIndex={currentQuestionIndex}
                          totalQuestions={visibleQuestions.length}
                          answer={answers[currentQuestion.id]}
                          onAnswer={(value) => {
                            setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
                            const typeInfo = getQuestionTypeInfo(currentQuestion.type);
                            if (typeInfo?.autoAdvance && value) {
                              setTimeout(handleNext, 300);
                            }
                          }}
                          onNext={handleNext}
                          onBack={handleBack}
                          canGoBack={true}
                          nextButtonText={form?.uiTextConfig?.nextButtonText || 'Next'}
                          backButtonText={form?.uiTextConfig?.backButtonText || 'Back'}
                          isMobile={isMobile}
                          editingEnabled={editingEnabled}
                          onUpdateQuestion={onUpdateQuestion ? (updates) => onUpdateQuestion(currentQuestion.id, updates) : undefined}
                        />
                      )}

                      {currentStep === 'thankyou' && (
                        <ThankYouStep
                          key="thankyou"
                          title={form?.uiTextConfig?.thankYouTitle || 'Thank you!'}
                          message={form?.uiTextConfig?.thankYouMessage || 'Your feedback helps us improve.'}
                          onReset={handleReset}
                          isMobile={isMobile}
                          editingEnabled={editingEnabled}
                          onUpdateTitle={onUpdateUIText ? (value) => onUpdateUIText('thankYouTitle', value) : undefined}
                          onUpdateMessage={onUpdateUIText ? (value) => onUpdateUIText('thankYouMessage', value) : undefined}
                        />
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Home Indicator */}
                  <div className="pb-2 pt-1 flex justify-center">
                    <div className="w-32 h-1 bg-foreground/20 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Desktop Preview */
          <div
            className={cn(
              'bg-background rounded-lg overflow-hidden border',
              'transition-all duration-300 w-full h-full'
            )}
          >
            {/* Progress Bar */}
            {form?.progressBarConfig?.enabled && (
              <div className="p-4 pb-0">
                <Progress value={progress} className="h-1" />
              </div>
            )}

            {/* Step Indicator */}
            {form?.stepIndicatorConfig?.enabled && (
              <div className="flex items-center justify-center gap-4 px-4 py-3">
                {form.stepIndicatorConfig.showArrows && (
                  <button
                    onClick={handleStepBack}
                    disabled={!canGoBackStep}
                    className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <span className="text-sm text-muted-foreground font-medium">
                  Step {currentStepNumber} / {totalSteps}
                </span>
                {form.stepIndicatorConfig.showArrows && (
                  <button
                    onClick={handleStepNext}
                    disabled={!canGoNextStep || currentStep === 'rating'}
                    className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            {/* Content Area */}
            <div className="h-full flex flex-col">
              <AnimatePresence mode="wait">
                {currentStep === 'rating' && (
                  <RatingStep
                    key="rating"
                    rating={rating}
                    onSelect={handleRatingSelect}
                    title={form?.uiTextConfig?.ratingTitle || 'How was your experience?'}
                    subtitle={form?.uiTextConfig?.ratingSubtitle || 'Tap a star to rate'}
                    isMobile={isMobile}
                    editingEnabled={editingEnabled}
                    onUpdateTitle={onUpdateUIText ? (value) => onUpdateUIText('ratingTitle', value) : undefined}
                    onUpdateSubtitle={onUpdateUIText ? (value) => onUpdateUIText('ratingSubtitle', value) : undefined}
                  />
                )}

                {currentStep === 'questions' && currentQuestion && (
                  <QuestionStep
                    key={currentQuestion.id}
                    question={currentQuestion}
                    questionIndex={currentQuestionIndex}
                    totalQuestions={visibleQuestions.length}
                    answer={answers[currentQuestion.id]}
                    onAnswer={(value) => {
                      setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
                      const typeInfo = getQuestionTypeInfo(currentQuestion.type);
                      if (typeInfo?.autoAdvance && value) {
                        setTimeout(handleNext, 300);
                      }
                    }}
                    onNext={handleNext}
                    onBack={handleBack}
                    canGoBack={true}
                    nextButtonText={form?.uiTextConfig?.nextButtonText || 'Next'}
                    backButtonText={form?.uiTextConfig?.backButtonText || 'Back'}
                    isMobile={isMobile}
                    editingEnabled={editingEnabled}
                    onUpdateQuestion={onUpdateQuestion ? (updates) => onUpdateQuestion(currentQuestion.id, updates) : undefined}
                  />
                )}

                {currentStep === 'thankyou' && (
                  <ThankYouStep
                    key="thankyou"
                    title={form?.uiTextConfig?.thankYouTitle || 'Thank you!'}
                    message={form?.uiTextConfig?.thankYouMessage || 'Your feedback helps us improve.'}
                    onReset={handleReset}
                    isMobile={isMobile}
                    editingEnabled={editingEnabled}
                    onUpdateTitle={onUpdateUIText ? (value) => onUpdateUIText('thankYouTitle', value) : undefined}
                    onUpdateMessage={onUpdateUIText ? (value) => onUpdateUIText('thankYouMessage', value) : undefined}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Animation variants
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
  }),
};

const transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
};

// Rating Step
function RatingStep({
  rating,
  onSelect,
  title,
  subtitle,
  isMobile,
  editingEnabled,
  onUpdateTitle,
  onUpdateSubtitle,
}: {
  rating: number;
  onSelect: (value: number) => void;
  title: string;
  subtitle: string;
  isMobile: boolean;
  editingEnabled?: boolean;
  onUpdateTitle?: (value: string) => void;
  onUpdateSubtitle?: (value: string) => void;
}) {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={transition}
      className={cn(
        'flex-1 flex flex-col items-center justify-center',
        isMobile ? 'px-6' : 'px-12'
      )}
    >
      <h2 className={cn('font-semibold text-center mb-2', isMobile ? 'text-xl' : 'text-2xl')}>
        {editingEnabled && onUpdateTitle ? (
          <InlineTextEditor
            value={title}
            onSave={onUpdateTitle}
            placeholder="How was your experience?"
            variant="light"
          />
        ) : (
          title
        )}
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        {editingEnabled && onUpdateSubtitle ? (
          <InlineTextEditor
            value={subtitle}
            onSave={onUpdateSubtitle}
            placeholder="Tap a star to rate"
            variant="light"
            className="text-muted-foreground"
          />
        ) : (
          subtitle
        )}
      </p>

      <div className={cn("flex", isMobile ? "gap-1" : "gap-2")}>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            onMouseEnter={() => setHoverRating(value)}
            onMouseLeave={() => setHoverRating(0)}
            className={cn("transition-transform hover:scale-110", isMobile ? "p-0.5" : "p-2")}
          >
            <Star
              className={cn(
                'transition-colors',
                isMobile ? 'h-10 w-10' : 'h-12 w-12',
                (hoverRating || rating) >= value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-muted-foreground'
              )}
            />
          </button>
        ))}
      </div>
    </motion.div>
  );
}

// Question Step
function QuestionStep({
  question,
  questionIndex,
  totalQuestions,
  answer,
  onAnswer,
  onNext,
  onBack,
  canGoBack,
  nextButtonText,
  backButtonText,
  isMobile,
  editingEnabled,
  onUpdateQuestion,
}: {
  question: FormQuestion;
  questionIndex: number;
  totalQuestions: number;
  answer: unknown;
  onAnswer: (value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
  nextButtonText: string;
  backButtonText: string;
  isMobile: boolean;
  editingEnabled?: boolean;
  onUpdateQuestion?: (updates: Partial<FormQuestion>) => void;
}) {
  const typeInfo = getQuestionTypeInfo(question.type);

  return (
    <motion.div
      initial="enter"
      animate="center"
      exit="exit"
      variants={slideVariants}
      custom={1}
      transition={transition}
      className={cn(
        'flex-1 flex flex-col',
        isMobile ? 'p-6' : 'p-12'
      )}
    >
      {/* Question Counter */}
      <div className="text-sm text-muted-foreground mb-4">
        Question {questionIndex + 1} of {totalQuestions}
      </div>

      {/* Question Text */}
      <h2 className={cn('font-semibold mb-2', isMobile ? 'text-lg' : 'text-xl')}>
        {editingEnabled && onUpdateQuestion ? (
          <InlineTextEditor
            value={question.text || ''}
            onSave={(value) => onUpdateQuestion({ text: value })}
            placeholder="Enter question text"
            variant="light"
          />
        ) : (
          question.text
        )}
        {question.required && <span className="text-destructive ml-1">*</span>}
      </h2>

      {question.uiConfig?.description && (
        <p className="text-muted-foreground mb-6">
          {editingEnabled && onUpdateQuestion ? (
            <InlineTextEditor
              value={question.uiConfig.description}
              onSave={(value) => onUpdateQuestion({
                uiConfig: { ...question.uiConfig, description: value }
              })}
              placeholder="Add description (optional)"
              variant="light"
              className="text-muted-foreground"
            />
          ) : (
            question.uiConfig.description
          )}
        </p>
      )}

      {/* Question Input */}
      <div className="flex-1 py-4">
        <QuestionInput
          question={question}
          value={answer}
          onChange={onAnswer}
          isMobile={isMobile}
        />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={!canGoBack}
          className="gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          {backButtonText}
        </Button>

        {!typeInfo?.autoAdvance && (
          <Button onClick={onNext} className="gap-2">
            {nextButtonText}
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
}

// Question Input Renderer
function QuestionInput({
  question,
  value,
  onChange,
  isMobile,
}: {
  question: FormQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  isMobile: boolean;
}) {
  switch (question.type) {
    case 'short_text':
    case 'email':
    case 'phone':
    case 'url':
      return (
        <Input
          type={question.type === 'email' ? 'email' : question.type === 'phone' ? 'tel' : 'text'}
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className={cn(isMobile ? 'text-base' : 'text-lg')}
        />
      );

    case 'long_text':
      return (
        <Textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          rows={4}
          className={cn(isMobile ? 'text-base' : 'text-lg')}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          min={question.validationRules?.min}
          max={question.validationRules?.max}
          className={cn(isMobile ? 'text-base' : 'text-lg')}
        />
      );

    case 'dropdown':
    case 'yes_no':
      return (
        <div className="space-y-2">
          {(question.options || []).map((option, idx) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left',
                value === option.value
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-muted-foreground/50'
              )}
            >
              {question.uiConfig?.showLetterIndicators && (
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                    value === option.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  {String.fromCharCode(65 + idx)}
                </div>
              )}
              <span className={cn(isMobile ? 'text-base' : 'text-lg')}>
                {option.label}
              </span>
              {value === option.value && (
                <Check className="h-5 w-5 ml-auto text-primary" />
              )}
            </button>
          ))}
        </div>
      );

    case 'checkboxes':
      const selectedValues = (value as string[]) || [];
      return (
        <div className="space-y-2">
          {(question.options || []).map((option, idx) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  if (isSelected) {
                    onChange(selectedValues.filter((v) => v !== option.value));
                  } else {
                    onChange([...selectedValues, option.value]);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-muted-foreground/50'
                )}
              >
                {question.uiConfig?.showLetterIndicators && (
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    )}
                  >
                    {String.fromCharCode(65 + idx)}
                  </div>
                )}
                <span className={cn(isMobile ? 'text-base' : 'text-lg')}>
                  {option.label}
                </span>
                {isSelected && <Check className="h-5 w-5 ml-auto text-primary" />}
              </button>
            );
          })}
        </div>
      );

    case 'rating':
      const ratingValue = (value as number) || 0;
      return (
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className="p-2 transition-transform hover:scale-110"
            >
              <Star
                className={cn(
                  'transition-colors',
                  isMobile ? 'h-6 w-6' : 'h-12 w-12',
                  ratingValue >= star
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-muted-foreground'
                )}
              />
            </button>
          ))}
        </div>
      );

    case 'opinion_scale':
      const min = question.validationRules?.min ?? 1;
      const max = question.validationRules?.max ?? 10;
      const scaleValue = (value as number) || 0;
      const scaleRange = Array.from({ length: max - min + 1 }, (_, i) => min + i);

      return (
        <div className="flex flex-wrap justify-center gap-2">
          {scaleRange.map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => onChange(num)}
              className={cn(
                'w-10 h-10 rounded-lg border font-medium transition-all',
                scaleValue === num
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'hover:border-muted-foreground/50'
              )}
            >
              {num}
            </button>
          ))}
        </div>
      );

    case 'date':
      return (
        <Input
          type="date"
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(isMobile ? 'text-base' : 'text-lg')}
        />
      );

    default:
      return (
        <div className="text-muted-foreground text-center py-8">
          Preview not available for {question.type}
        </div>
      );
  }
}

// Thank You Step
function ThankYouStep({
  title,
  message,
  onReset,
  isMobile,
  editingEnabled,
  onUpdateTitle,
  onUpdateMessage,
}: {
  title: string;
  message: string;
  onReset: () => void;
  isMobile: boolean;
  editingEnabled?: boolean;
  onUpdateTitle?: (value: string) => void;
  onUpdateMessage?: (value: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={transition}
      className={cn(
        'flex-1 flex flex-col items-center justify-center',
        isMobile ? 'px-6' : 'px-12'
      )}
    >
      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
        <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>

      <h2 className={cn('font-semibold text-center mb-2', isMobile ? 'text-xl' : 'text-2xl')}>
        {editingEnabled && onUpdateTitle ? (
          <InlineTextEditor
            value={title}
            onSave={onUpdateTitle}
            placeholder="Thank you!"
            variant="light"
          />
        ) : (
          title
        )}
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        {editingEnabled && onUpdateMessage ? (
          <InlineTextEditor
            value={message}
            onSave={onUpdateMessage}
            placeholder="Your feedback helps us improve."
            variant="light"
            className="text-muted-foreground"
          />
        ) : (
          message
        )}
      </p>

      <Button variant="outline" onClick={onReset}>
        Preview Again
      </Button>
    </motion.div>
  );
}
